import { useState, useEffect } from 'react';
import { CREDIT_LIMITS } from '../utils/constants.js';

/**
 * Custom hook for managing course selection state
 * Handles selected/backup course IDs, selected course codes, and toggle logic
 * @param {Object} groupedCourses - All grouped courses
 * @param {Object} semesterCredits - Credits per semester
 * @returns {Object} - Selection state and handlers
 */
export const useCourseSelection = (groupedCourses, semesterCredits) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [backupIds, setBackupIds] = useState(new Set());
  const [selectedCourses, setSelectedCourses] = useState(new Set());

  // Update selectedCourses whenever selectedIds or backupIds change
  useEffect(() => {
    const allSelectedIds = new Set([...selectedIds, ...backupIds]);
    const selectedCourseCodes = new Set();
    
    Object.values(groupedCourses).forEach(group => {
      group.courses.forEach(course => {
        const courseId = course.is_split_course 
          ? `${course.semester_instance}_p${course.actual_period}` 
          : course.semester_instance;
        
        if (allSelectedIds.has(courseId)) {
          selectedCourseCodes.add(course.course_code);
        }
      });
    });
    
    setSelectedCourses(selectedCourseCodes);
  }, [selectedIds, backupIds, groupedCourses]);

  /**
   * Toggle course selection between unselected → selected → backup → unselected
   * @param {string} id - Course ID to toggle
   */
  const toggleSelect = (id) => {
    // Find the course
    let course = null;
    Object.values(groupedCourses).forEach(group => {
      const foundCourse = group.courses.find(c => {
        const courseId = c.is_split_course 
          ? `${c.semester_instance}_p${c.actual_period}` 
          : c.semester_instance;
        return courseId === id;
      });
      if (foundCourse) course = foundCourse;
    });

    if (!course) return;

    setSelectedIds((prevSelected) => {
      const newSelected = new Set(prevSelected);
      
      setBackupIds((prevBackup) => {
        const newBackup = new Set(prevBackup);

        // Handle split courses
        if (course.is_split_course) {
          // Find all parts of this specific instance of the split course
          const allCourseParts = Object.values(groupedCourses)
            .flatMap(group => group.courses)
            .filter(c => c.is_split_course && c.semester_instance === course.semester_instance)
            .map(c => `${c.semester_instance}_p${c.actual_period}`);
          
          // Check current state of the course parts
          const anyPartSelected = allCourseParts.some(partId => newSelected.has(partId));
          const anyPartBackup = allCourseParts.some(partId => newBackup.has(partId));
          
          if (anyPartSelected) {
            // Selected → Backup: Remove from selected, add to backup
            allCourseParts.forEach(partId => {
              newSelected.delete(partId);
              newBackup.add(partId);
            });
          } else if (anyPartBackup) {
            // Backup → Unselected: Remove from backup
            allCourseParts.forEach(partId => newBackup.delete(partId));
          } else {
            // Unselected → Selected/Backup: Check if this course is already selected elsewhere
            
            // Build a set of actually selected course codes (excluding backup)
            const actuallySelectedCourses = new Set();
            Object.values(groupedCourses).forEach(group => {
              group.courses.forEach(c => {
                const cId = c.is_split_course 
                  ? `${c.semester_instance}_p${c.actual_period}` 
                  : c.semester_instance;
                
                if (newSelected.has(cId)) {
                  actuallySelectedCourses.add(c.course_code);
                }
              });
            });

            if (actuallySelectedCourses.has(course.course_code)) {
              // Course is already selected elsewhere - go directly to backup
              allCourseParts.forEach(partId => newBackup.add(partId));
            } else {
              // Course not selected elsewhere - try to select if within credit limits
              const totalCreditsForAllParts = Object.values(groupedCourses)
                .flatMap(group => group.courses)
                .filter(c => c.is_split_course && c.semester_instance === course.semester_instance)
                .reduce((sum, c) => sum + c.credits, 0);
              
              // Find the semester for this course
              const group = Object.values(groupedCourses)
                .find(group => group.courses.some(c => {
                  const courseId = c.is_split_course 
                    ? `${c.semester_instance}_p${c.actual_period}` 
                    : c.semester_instance;
                  return courseId === id;
                }));
              
              if (group) {
                const semesterKey = `${group.year}_s${group.semester}`;
                const currentCredits = semesterCredits[semesterKey] || 0;
                
                if (currentCredits + totalCreditsForAllParts <= CREDIT_LIMITS.SEMESTER_MAX) {
                  allCourseParts.forEach(partId => newSelected.add(partId));
                }
                // If it would exceed 45 credits, don't add it (stay unselected)
              }
            }
          }
        } else {
          // Handle regular single courses
          if (newSelected.has(id)) {
            // Selected → Backup: Remove from selected, add to backup
            newSelected.delete(id);
            newBackup.add(id);
          } else if (newBackup.has(id)) {
            // Backup → Unselected: Remove from backup
            newBackup.delete(id);
          } else {
            // Unselected → Selected/Backup: Check if this course is already selected elsewhere
            
            // Build a set of actually selected course codes (excluding backup)
            const actuallySelectedCourses = new Set();
            Object.values(groupedCourses).forEach(group => {
              group.courses.forEach(c => {
                const cId = c.is_split_course 
                  ? `${c.semester_instance}_p${c.actual_period}` 
                  : c.semester_instance;
                
                if (newSelected.has(cId)) {
                  actuallySelectedCourses.add(c.course_code);
                }
              });
            });

            if (actuallySelectedCourses.has(course.course_code)) {
              // Course is already selected elsewhere - go directly to backup
              newBackup.add(id);
            } else {
              // Course not selected elsewhere - try to select if within credit limits
              const group = Object.values(groupedCourses)
                .find(group => group.courses.some(c => {
                  const courseId = c.is_split_course 
                    ? `${c.semester_instance}_p${c.actual_period}` 
                    : c.semester_instance;
                  return courseId === id;
                }));
              
              if (group) {
                const semesterKey = `${group.year}_s${group.semester}`;
                const currentCredits = semesterCredits[semesterKey] || 0;
                
                if (currentCredits + course.credits <= CREDIT_LIMITS.SEMESTER_MAX) {
                  newSelected.add(id);
                }
                // If it would exceed 45 credits, don't add it (stay unselected)
              }
            }
          }
        }
        
        return newBackup;
      });
      
      return newSelected;
    });
  };

  return {
    selectedIds,
    backupIds,
    selectedCourses,
    setSelectedIds,
    setBackupIds,
    setSelectedCourses,
    toggleSelect
  };
}; 